import { InjectionToken, Provider } from '@angular/core';
import type { HelpShortcutGroup } from '../components/modals/help-shortcuts.component';
import type { OutlineErrorCode } from '../utils/outline-adapter';

export type FlashMapLanguage = 'en' | 'pt' | 'es';

export interface FlashMapI18nConfig {
  language?: FlashMapLanguage | string | null;
}

export interface FlashMapToolbarTranslations {
  helpTitle: string;
  helpAriaLabel: string;
  downloadTitle: string;
  downloadAriaLabel: string;
  shareTitle: string;
  shareAriaLabel: string;
}

export interface FlashMapHelpTranslations {
  closeAriaLabel: string;
  keySeparator: string;
}

export interface FlashMapOutlineTranslations {
  expand: string;
  collapse: string;
  addItem: string;
  addItemTitle: string;
  deleteItem: string;
  deleteItemTitle: string;
  newItem: string;
  editorAriaLabel: string;
  lineLabel: string;
  errors: Record<OutlineErrorCode, string>;
}

export interface FlashMapPresentationTranslations {
  slide: string;
  panelAriaLabel: string;
  previousAriaLabel: string;
  nextAriaLabel: string;
  closeAriaLabel: string;
}

export interface FlashMapStyleToolbarTranslations {
  style: string;
  textColor: string;
  addNote: string;
  decks: string;
  shape: string;
  border: string;
  line: string;
  themeColors: string;
  customColor: string;
  colorTheme: string;
  font: string;
  fontDefault: string;
  fontBrushScript: string;
  fontComicSans: string;
  fontSerif: string;
  format: string;
  square: string;
  thin: string;
  medium: string;
  thick: string;
  borderWidth: string;
  borderStyle: string;
  solid: string;
  dashed: string;
  dotted: string;
}

export interface FlashMapDrawingTranslations {
  panelAriaLabel: string;
  arrangeToolbarAriaLabel: string;
  penTitle: string;
  penAriaLabel: string;
  laserTitle: string;
  laserAriaLabel: string;
  rectangleTitle: string;
  rectangleAriaLabel: string;
  ellipseTitle: string;
  ellipseAriaLabel: string;
  arrowTitle: string;
  arrowAriaLabel: string;
  strokeColorTitle: string;
  strokeColorAriaLabel: string;
  fillColorTitle: string;
  fillColorAriaLabel: string;
  customColorAriaLabel: string;
  noFillTitle: string;
  strokeWidthTitle: string;
  strokeWidthAriaLabel: string;
  strokeWidthThin: string;
  strokeWidthMedium: string;
  strokeWidthThick: string;
  strokeStyleTitle: string;
  strokeStyleAriaLabel: string;
  strokeStyleSolid: string;
  strokeStyleDashed: string;
  bringForwardTitle: string;
  bringForwardAriaLabel: string;
  sendBackwardTitle: string;
  sendBackwardAriaLabel: string;
  bringInFrontOfNodesTitle: string;
  bringInFrontOfNodesAriaLabel: string;
  sendBehindNodesTitle: string;
  sendBehindNodesAriaLabel: string;
  clearTitle: string;
  clearAriaLabel: string;
}

export interface FlashMapTranslations {
  language: FlashMapLanguage;
  untitled: string;
  helpTitleMap: string;
  helpTitleOutline: string;
  mapShortcuts: HelpShortcutGroup[];
  outlineShortcuts: HelpShortcutGroup[];
  toolbar: FlashMapToolbarTranslations;
  styleToolbar: FlashMapStyleToolbarTranslations;
  drawing: FlashMapDrawingTranslations;
  help: FlashMapHelpTranslations;
  outline: FlashMapOutlineTranslations;
  presentation: FlashMapPresentationTranslations;
}

export const FLASH_MAP_I18N_CONFIG = new InjectionToken<FlashMapI18nConfig>('FLASH_MAP_I18N_CONFIG', {
  providedIn: 'root',
  factory: () => ({ language: 'en' }),
});

export const provideFlashMapI18n = (config: FlashMapI18nConfig): Provider => ({
  provide: FLASH_MAP_I18N_CONFIG,
  useValue: config,
});

const outlineErrorsEn: Record<OutlineErrorCode, string> = {
  invalidIndentation: 'Invalid indentation (use TAB or 2 spaces).',
  onlyFirstLineRoot: 'Only the first line can be the root.',
  invalidIndentationLevel: 'Invalid indentation level.',
  rootMustBeLevelZero: 'The root must be at level 0.',
  parentNotFound: 'Could not determine the parent.',
  invalidOutline: 'Invalid outline.',
  emptyOutline: 'Empty outline.',
};

const outlineErrorsPt: Record<OutlineErrorCode, string> = {
  invalidIndentation: 'Indentação inválida (use TAB ou 2 espaços).',
  onlyFirstLineRoot: 'Somente a primeira linha pode ser raiz.',
  invalidIndentationLevel: 'Nível de indentação inválido.',
  rootMustBeLevelZero: 'A raiz deve estar no nível 0.',
  parentNotFound: 'Não foi possível determinar o pai.',
  invalidOutline: 'Outline inválido.',
  emptyOutline: 'Outline vazio.',
};

const outlineErrorsEs: Record<OutlineErrorCode, string> = {
  invalidIndentation: 'Sangría inválida (use TAB o 2 espacios).',
  onlyFirstLineRoot: 'Solo la primera línea puede ser la raíz.',
  invalidIndentationLevel: 'Nivel de sangría inválido.',
  rootMustBeLevelZero: 'La raíz debe estar en el nivel 0.',
  parentNotFound: 'No se pudo determinar el padre.',
  invalidOutline: 'Outline inválido.',
  emptyOutline: 'Outline vacío.',
};

export const FLASH_MAP_TRANSLATIONS: Record<FlashMapLanguage, FlashMapTranslations> = {
  en: {
    language: 'en',
    untitled: 'Untitled',
    helpTitleMap: 'Keyboard Shortcuts',
    helpTitleOutline: 'Outline Shortcuts',
    mapShortcuts: [
      {
        title: 'General',
        desktopColumn: 'left',
        shortcuts: [
          { keys: ['Enter'], label: 'Add sibling' },
          { keys: ['Insert', 'Ctrl+Enter'], label: 'Add child' },
          { keys: ['F2'], label: 'Edit node' },
          { keys: ['Delete'], label: 'Remove node' },
          { keys: ['Ctrl+C'], label: 'Copy node' },
          { keys: ['Ctrl+X'], label: 'Cut node' },
          { keys: ['Ctrl+V'], label: 'Paste node' },
          { keys: ['Ctrl+F'], label: 'Search map' },
          { keys: ['Alt+P'], label: 'Start presentation' },
          { keys: ['Shift+D'], label: 'Toggle drawing tools' },
          { keys: ['Shift+P'], label: 'Toggle pen' },
          { keys: ['Shift+L'], label: 'Toggle laser' },
        ],
      },
      {
        title: 'Navigation',
        desktopColumn: 'right',
        shortcuts: [
          { keys: ['Arrow keys'], label: 'Move selection' },
          { keys: ['Ctrl+Up'], label: 'Move up' },
          { keys: ['Ctrl+Down'], label: 'Move down' },
          { keys: ['Space'], label: 'Expand/collapse children' },
        ],
      },
      {
        title: 'Modes',
        desktopColumn: 'right',
        shortcuts: [{ keys: ['Alt+Shift+O'], label: 'Switch to outline' }],
      },
      {
        title: 'Formatting',
        desktopColumn: 'right',
        shortcuts: [
          { keys: ['Ctrl+B'], label: 'Bold' },
          { keys: ['Ctrl+I'], label: 'Italic' },
        ],
      },
    ],
    outlineShortcuts: [
      {
        title: 'General',
        desktopColumn: 'left',
        shortcuts: [
          { keys: ['Enter'], label: 'Add sibling' },
          { keys: ['Tab'], label: 'Indent (make child)' },
          { keys: ['Backspace'], label: 'Outdent' },
          { keys: ['F2'], label: 'Edit item' },
          { keys: ['Delete'], label: 'Remove item' },
          { keys: ['Ctrl+F'], label: 'Search map' },
        ],
      },
      {
        title: 'Navigation',
        desktopColumn: 'right',
        shortcuts: [{ keys: ['Arrow keys'], label: 'Navigate' }],
      },
      {
        title: 'Modes',
        desktopColumn: 'right',
        shortcuts: [{ keys: ['Alt+Shift+O'], label: 'Switch to mind map' }],
      },
    ],
    toolbar: {
      helpTitle: 'Help',
      helpAriaLabel: 'Open help',
      downloadTitle: 'Download',
      downloadAriaLabel: 'Download',
      shareTitle: 'Share',
      shareAriaLabel: 'Share',
    },
    styleToolbar: {
      style: 'Style',
      textColor: 'Text color',
      addNote: 'Add note',
      decks: 'Decks',
      shape: 'Shape',
      border: 'Border',
      line: 'Line',
      themeColors: 'Theme colors',
      customColor: 'Custom color',
      colorTheme: 'ColorTheme',
      font: 'Font',
      fontDefault: 'Default',
      fontBrushScript: 'Dancing Script',
      fontComicSans: 'Comic Sans MS',
      fontSerif: 'Serif',
      format: 'Format',
      square: 'Square',
      thin: 'Thin',
      medium: 'Medium',
      thick: 'Thick',
      borderWidth: 'Width',
      borderStyle: 'Stroke',
      solid: 'Solid',
      dashed: 'Dashed',
        dotted: 'Dotted',
      },
      drawing: {
        panelAriaLabel: 'Drawing tools',
        arrangeToolbarAriaLabel: 'Drawing arrange controls',
        penTitle: 'Pen (Shift+P)',
        penAriaLabel: 'Toggle pen',
        laserTitle: 'Laser (Shift+L)',
        laserAriaLabel: 'Toggle laser',
        rectangleTitle: 'Rectangle',
        rectangleAriaLabel: 'Toggle rectangle',
        ellipseTitle: 'Ellipse',
        ellipseAriaLabel: 'Toggle ellipse',
        arrowTitle: 'Arrow',
        arrowAriaLabel: 'Toggle arrow',
        strokeColorTitle: 'Stroke color',
        strokeColorAriaLabel: 'Choose stroke color',
        fillColorTitle: 'Fill color',
        fillColorAriaLabel: 'Choose fill color',
        customColorAriaLabel: 'Custom color',
        noFillTitle: 'No fill',
        strokeWidthTitle: 'Stroke width',
        strokeWidthAriaLabel: 'Select stroke width',
        strokeWidthThin: 'Thin',
        strokeWidthMedium: 'Medium',
        strokeWidthThick: 'Thick',
        strokeStyleTitle: 'Stroke style',
        strokeStyleAriaLabel: 'Select stroke style',
        strokeStyleSolid: 'Solid',
        strokeStyleDashed: 'Dashed',
        bringForwardTitle: 'Bring forward',
        bringForwardAriaLabel: 'Bring selected drawing forward',
        sendBackwardTitle: 'Send backward',
        sendBackwardAriaLabel: 'Send selected drawing backward',
        bringInFrontOfNodesTitle: 'In front of nodes',
        bringInFrontOfNodesAriaLabel: 'Bring selected drawing in front of nodes',
        sendBehindNodesTitle: 'Behind nodes',
        sendBehindNodesAriaLabel: 'Send selected drawing behind nodes',
        clearTitle: 'Clear drawings',
        clearAriaLabel: 'Clear drawings',
      },
      help: {
      closeAriaLabel: 'Close help',
      keySeparator: 'or',
    },
    outline: {
      expand: 'Expand',
      collapse: 'Collapse',
      addItem: 'Add new item',
      addItemTitle: 'Add new item (Enter)',
      deleteItem: 'Delete item',
      deleteItemTitle: 'Delete item (Delete)',
      newItem: 'New item',
      editorAriaLabel: 'Mind map outline',
      lineLabel: 'line',
      errors: outlineErrorsEn,
    },
    presentation: {
      slide: 'Slide',
      panelAriaLabel: 'Presentation controls',
      previousAriaLabel: 'Previous slide',
      nextAriaLabel: 'Next slide',
      closeAriaLabel: 'Exit presentation',
    },
  },
  pt: {
    language: 'pt',
    untitled: 'Sem título',
    helpTitleMap: 'Atalhos do teclado',
    helpTitleOutline: 'Atalhos do outline',
    mapShortcuts: [
      {
        title: 'Geral',
        desktopColumn: 'left',
        shortcuts: [
          { keys: ['Enter'], label: 'Adicionar irmão' },
          { keys: ['Insert', 'Ctrl+Enter'], label: 'Adicionar filho' },
          { keys: ['F2'], label: 'Editar nó' },
          { keys: ['Delete'], label: 'Remover nó' },
          { keys: ['Ctrl+C'], label: 'Copiar nó' },
          { keys: ['Ctrl+X'], label: 'Recortar nó' },
          { keys: ['Ctrl+V'], label: 'Colar nó' },
          { keys: ['Ctrl+F'], label: 'Buscar no mapa' },
          { keys: ['Alt+P'], label: 'Iniciar apresentação' },
          { keys: ['Shift+D'], label: 'Alternar ferramentas de desenho' },
          { keys: ['Shift+P'], label: 'Alternar caneta' },
          { keys: ['Shift+L'], label: 'Alternar laser' },
        ],
      },
      {
        title: 'Navegação',
        desktopColumn: 'right',
        shortcuts: [
          { keys: ['Setas'], label: 'Mover seleção' },
          { keys: ['Ctrl+Up'], label: 'Mover para cima' },
          { keys: ['Ctrl+Down'], label: 'Mover para baixo' },
          { keys: ['Space'], label: 'Expandir/recolher filhos' },
        ],
      },
      {
        title: 'Modos',
        desktopColumn: 'right',
        shortcuts: [{ keys: ['Alt+Shift+O'], label: 'Alternar para outline' }],
      },
      {
        title: 'Formatação',
        desktopColumn: 'right',
        shortcuts: [
          { keys: ['Ctrl+B'], label: 'Negrito' },
          { keys: ['Ctrl+I'], label: 'Itálico' },
        ],
      },
    ],
    outlineShortcuts: [
      {
        title: 'Geral',
        desktopColumn: 'left',
        shortcuts: [
          { keys: ['Enter'], label: 'Adicionar irmão' },
          { keys: ['Tab'], label: 'Indentar (virar filho)' },
          { keys: ['Backspace'], label: 'Desindentar' },
          { keys: ['F2'], label: 'Editar item' },
          { keys: ['Delete'], label: 'Remover item' },
          { keys: ['Ctrl+F'], label: 'Buscar no mapa' },
        ],
      },
      {
        title: 'Navegação',
        desktopColumn: 'right',
        shortcuts: [{ keys: ['Setas'], label: 'Navegar' }],
      },
      {
        title: 'Modos',
        desktopColumn: 'right',
        shortcuts: [{ keys: ['Alt+Shift+O'], label: 'Alternar para mindmap' }],
      },
    ],
    toolbar: {
      helpTitle: 'Ajuda',
      helpAriaLabel: 'Abrir ajuda',
      downloadTitle: 'Baixar',
      downloadAriaLabel: 'Baixar',
      shareTitle: 'Compartilhar',
      shareAriaLabel: 'Compartilhar',
    },
    styleToolbar: {
      style: 'Estilo',
      textColor: 'Cor do texto',
      addNote: 'Adicionar anotação',
      decks: 'Decks',
      shape: 'Forma',
      border: 'Borda',
      line: 'Linha',
      themeColors: 'Cores do tema',
      customColor: 'Cor personalizada',
      colorTheme: 'ColorTheme',
      font: 'Fonte',
      fontDefault: 'Padrão',
      fontBrushScript: 'Dancing Script',
      fontComicSans: 'Comic Sans MS',
      fontSerif: 'Serif',
      format: 'Formato',
      square: 'Quadrado',
      thin: 'Fina',
      medium: 'Média',
      thick: 'Larga',
      borderWidth: 'Grossura',
      borderStyle: 'Traço',
      solid: 'Sólido',
      dashed: 'Tracejado',
        dotted: 'Pontilhado',
      },
      drawing: {
        panelAriaLabel: 'Ferramentas de desenho',
        arrangeToolbarAriaLabel: 'Controles de organizacao do desenho',
        penTitle: 'Caneta (Shift+P)',
        penAriaLabel: 'Alternar caneta',
        laserTitle: 'Laser (Shift+L)',
        laserAriaLabel: 'Alternar laser',
        rectangleTitle: 'Retângulo',
        rectangleAriaLabel: 'Alternar retângulo',
        ellipseTitle: 'Elipse',
        ellipseAriaLabel: 'Alternar elipse',
        arrowTitle: 'Seta',
        arrowAriaLabel: 'Alternar seta',
        strokeColorTitle: 'Cor da borda',
        strokeColorAriaLabel: 'Escolher cor da borda',
        fillColorTitle: 'Cor do preenchimento',
        fillColorAriaLabel: 'Escolher cor do preenchimento',
        customColorAriaLabel: 'Cor personalizada',
        noFillTitle: 'Sem preenchimento',
        strokeWidthTitle: 'Grossura do traço',
        strokeWidthAriaLabel: 'Selecionar grossura do traço',
        strokeWidthThin: 'Fina',
        strokeWidthMedium: 'Média',
        strokeWidthThick: 'Grossa',
        strokeStyleTitle: 'Estilo do traço',
        strokeStyleAriaLabel: 'Selecionar estilo do traço',
        strokeStyleSolid: 'Sólido',
        strokeStyleDashed: 'Tracejado',
        bringForwardTitle: 'Trazer para frente',
        bringForwardAriaLabel: 'Trazer desenho selecionado para frente',
        sendBackwardTitle: 'Enviar para tras',
        sendBackwardAriaLabel: 'Enviar desenho selecionado para tras',
        bringInFrontOfNodesTitle: 'Na frente dos nos',
        bringInFrontOfNodesAriaLabel: 'Colocar desenho selecionado na frente dos nos',
        sendBehindNodesTitle: 'Atras dos nos',
        sendBehindNodesAriaLabel: 'Colocar desenho selecionado atras dos nos',
        clearTitle: 'Limpar desenhos',
        clearAriaLabel: 'Limpar desenhos',
      },
      help: {
      closeAriaLabel: 'Fechar ajuda',
      keySeparator: 'ou',
    },
    outline: {
      expand: 'Expandir',
      collapse: 'Colapsar',
      addItem: 'Adicionar novo item',
      addItemTitle: 'Adicionar novo item (Enter)',
      deleteItem: 'Deletar item',
      deleteItemTitle: 'Deletar item (Delete)',
      newItem: 'Novo item',
      editorAriaLabel: 'Outline do mindmap',
      lineLabel: 'linha',
      errors: outlineErrorsPt,
    },
    presentation: {
      slide: 'Slide',
      panelAriaLabel: 'Controles da apresentacao',
      previousAriaLabel: 'Slide anterior',
      nextAriaLabel: 'Proximo slide',
      closeAriaLabel: 'Sair da apresentacao',
    },
  },
  es: {
    language: 'es',
    untitled: 'Sin título',
    helpTitleMap: 'Atajos de teclado',
    helpTitleOutline: 'Atajos del outline',
    mapShortcuts: [
      {
        title: 'General',
        desktopColumn: 'left',
        shortcuts: [
          { keys: ['Enter'], label: 'Agregar hermano' },
          { keys: ['Insert', 'Ctrl+Enter'], label: 'Agregar hijo' },
          { keys: ['F2'], label: 'Editar nodo' },
          { keys: ['Delete'], label: 'Eliminar nodo' },
          { keys: ['Ctrl+C'], label: 'Copiar nodo' },
          { keys: ['Ctrl+X'], label: 'Cortar nodo' },
          { keys: ['Ctrl+V'], label: 'Pegar nodo' },
          { keys: ['Ctrl+F'], label: 'Buscar en el mapa' },
          { keys: ['Alt+P'], label: 'Iniciar presentación' },
          { keys: ['Shift+D'], label: 'Alternar herramientas de dibujo' },
          { keys: ['Shift+P'], label: 'Alternar bolígrafo' },
          { keys: ['Shift+L'], label: 'Alternar láser' },
        ],
      },
      {
        title: 'Navegación',
        desktopColumn: 'right',
        shortcuts: [
          { keys: ['Flechas'], label: 'Mover selección' },
          { keys: ['Ctrl+Up'], label: 'Mover hacia arriba' },
          { keys: ['Ctrl+Down'], label: 'Mover hacia abajo' },
          { keys: ['Space'], label: 'Expandir/contraer hijos' },
        ],
      },
      {
        title: 'Modos',
        desktopColumn: 'right',
        shortcuts: [{ keys: ['Alt+Shift+O'], label: 'Cambiar a outline' }],
      },
      {
        title: 'Formato',
        desktopColumn: 'right',
        shortcuts: [
          { keys: ['Ctrl+B'], label: 'Negrita' },
          { keys: ['Ctrl+I'], label: 'Cursiva' },
        ],
      },
    ],
    outlineShortcuts: [
      {
        title: 'General',
        desktopColumn: 'left',
        shortcuts: [
          { keys: ['Enter'], label: 'Agregar hermano' },
          { keys: ['Tab'], label: 'Indentar (hacer hijo)' },
          { keys: ['Backspace'], label: 'Desindentar' },
          { keys: ['F2'], label: 'Editar item' },
          { keys: ['Delete'], label: 'Eliminar item' },
          { keys: ['Ctrl+F'], label: 'Buscar en el mapa' },
        ],
      },
      {
        title: 'Navegación',
        desktopColumn: 'right',
        shortcuts: [{ keys: ['Flechas'], label: 'Navegar' }],
      },
      {
        title: 'Modos',
        desktopColumn: 'right',
        shortcuts: [{ keys: ['Alt+Shift+O'], label: 'Cambiar a mind map' }],
      },
    ],
    toolbar: {
      helpTitle: 'Ayuda',
      helpAriaLabel: 'Abrir ayuda',
      downloadTitle: 'Descargar',
      downloadAriaLabel: 'Descargar',
      shareTitle: 'Compartir',
      shareAriaLabel: 'Compartir',
    },
    styleToolbar: {
      style: 'Estilo',
      textColor: 'Color del texto',
      addNote: 'Agregar nota',
      decks: 'Mazos',
      shape: 'Forma',
      border: 'Borde',
      line: 'Línea',
      themeColors: 'Colores del tema',
      customColor: 'Color personalizado',
      colorTheme: 'ColorTheme',
      font: 'Fuente',
      fontDefault: 'Predeterminada',
      fontBrushScript: 'Dancing Script',
      fontComicSans: 'Comic Sans MS',
      fontSerif: 'Serif',
      format: 'Formato',
      square: 'Cuadrado',
      thin: 'Fino',
      medium: 'Medio',
      thick: 'Grueso',
      borderWidth: 'Grosor',
      borderStyle: 'Trazo',
      solid: 'Sólido',
      dashed: 'Discontinuo',
        dotted: 'Punteado',
      },
      drawing: {
        panelAriaLabel: 'Herramientas de dibujo',
        arrangeToolbarAriaLabel: 'Controles de orden del dibujo',
        penTitle: 'Bolígrafo (Shift+P)',
        penAriaLabel: 'Alternar bolígrafo',
        laserTitle: 'Láser (Shift+L)',
        laserAriaLabel: 'Alternar láser',
        rectangleTitle: 'Rectángulo',
        rectangleAriaLabel: 'Alternar rectángulo',
        ellipseTitle: 'Elipse',
        ellipseAriaLabel: 'Alternar elipse',
        arrowTitle: 'Flecha',
        arrowAriaLabel: 'Alternar flecha',
        strokeColorTitle: 'Color del borde',
        strokeColorAriaLabel: 'Elegir color del borde',
        fillColorTitle: 'Color de relleno',
        fillColorAriaLabel: 'Elegir color de relleno',
        customColorAriaLabel: 'Color personalizado',
        noFillTitle: 'Sin relleno',
        strokeWidthTitle: 'Grosor del trazo',
        strokeWidthAriaLabel: 'Seleccionar grosor del trazo',
        strokeWidthThin: 'Fino',
        strokeWidthMedium: 'Medio',
        strokeWidthThick: 'Grueso',
        strokeStyleTitle: 'Estilo del trazo',
        strokeStyleAriaLabel: 'Seleccionar estilo del trazo',
        strokeStyleSolid: 'Sólido',
        strokeStyleDashed: 'Discontinuo',
        bringForwardTitle: 'Traer al frente',
        bringForwardAriaLabel: 'Traer dibujo seleccionado al frente',
        sendBackwardTitle: 'Enviar atras',
        sendBackwardAriaLabel: 'Enviar dibujo seleccionado atras',
        bringInFrontOfNodesTitle: 'Delante de nodos',
        bringInFrontOfNodesAriaLabel: 'Colocar dibujo seleccionado delante de los nodos',
        sendBehindNodesTitle: 'Detras de nodos',
        sendBehindNodesAriaLabel: 'Colocar dibujo seleccionado detras de los nodos',
        clearTitle: 'Limpiar dibujos',
        clearAriaLabel: 'Limpiar dibujos',
      },
      help: {
      closeAriaLabel: 'Cerrar ayuda',
      keySeparator: 'o',
    },
    outline: {
      expand: 'Expandir',
      collapse: 'Contraer',
      addItem: 'Agregar nuevo item',
      addItemTitle: 'Agregar nuevo item (Enter)',
      deleteItem: 'Eliminar item',
      deleteItemTitle: 'Eliminar item (Delete)',
      newItem: 'Nuevo item',
      editorAriaLabel: 'Outline del mind map',
      lineLabel: 'línea',
      errors: outlineErrorsEs,
    },
    presentation: {
      slide: 'Slide',
      panelAriaLabel: 'Controles de presentacion',
      previousAriaLabel: 'Slide anterior',
      nextAriaLabel: 'Siguiente slide',
      closeAriaLabel: 'Salir de la presentacion',
    },
  },
};

export const normalizeFlashMapLanguage = (
  language: FlashMapLanguage | string | null | undefined
): FlashMapLanguage => {
  if (!language) {
    return 'en';
  }
  const normalized = language.toLowerCase().split(/[-_]/)[0];
  if (normalized === 'pt' || normalized === 'es' || normalized === 'en') {
    return normalized;
  }
  return 'en';
};

export const getFlashMapTranslations = (
  language: FlashMapLanguage | string | null | undefined
): FlashMapTranslations => FLASH_MAP_TRANSLATIONS[normalizeFlashMapLanguage(language)];
